import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { Stepper } from '@sorare/core/src/components/onboarding/Dialog/Stepper';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import background from './assets/background.jpg';

const StyledDialog = styled(Dialog)`
  background:
    linear-gradient(var(--background-color), var(--background-color)),
    url(${background}) no-repeat center center;
  background-size: cover;
  background-blend-mode: overlay;
`;
const Root = styled(Vertical)`
  height: 100%;
  position: relative;
  isolation: isolate;
`;
const Wrapper = styled(Vertical)`
  justify-content: space-between;
  height: 100%;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 2;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const TitleWrapper = styled.div`
  padding: 0 var(--double-unit);
`;

const SlidingContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  transition: transform 0.3s ease;
  height: 100%;
`;

const StepperWrapper = styled(Horizontal).attrs({ center: true })`
  width: 112px;
  align-self: center;
  padding: var(--quadruple-unit) 0 var(--double-unit);
`;
const StepContent = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: var(--double-unit);
  flex: 1;
  width: 100%;
`;
const StepWrapper = styled(Vertical).attrs({ gap: 3 })`
  align-items: stretch;
`;
const Step = styled.div`
  min-width: 100%;
  display: grid;
  padding: 0 var(--double-unit);
  > * {
    padding-top: calc(4 * var(--unit));
    padding-bottom: calc(10 * var(--unit));
    width: 100%;
  }
`;

const FooterButtons = styled(Horizontal).attrs({ gap: 2 })`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
  &:before {
    position: absolute;
    inset: -100% 0 0 0;
    content: '';
    background: linear-gradient(
      to bottom,
      transparent 20%,
      var(--c-nl-500),
      var(--c-black)
    );
  }
  & > * {
    flex: 1;
  }
`;

const messages = defineMessages({
  step1Title: {
    id: 'Season26ReactivationDialog.step1.title',
    defaultMessage: 'In-Season Competitions',
  },
  step1Item1: {
    id: 'Season26ReactivationDialog.step1.list.item1',
    defaultMessage: '7 league comps + Challenger & Contender',
  },
  step1Item2: {
    id: 'Season26ReactivationDialog.step1.list.item2',
    defaultMessage: 'Enter up to 4 lineups per competition',
  },
  step2Title: {
    id: 'Season26ReactivationDialog.step2.title',
    defaultMessage: 'Hot Streaks Return',
  },
  step2Item1: {
    id: 'Season26ReactivationDialog.step2.list.item1',
    defaultMessage: 'Weekly score targets = real cash rewards',
  },
  step2Item2: {
    id: 'Season26ReactivationDialog.step2.list.item2',
    defaultMessage: 'Double rewards until end of September',
  },
  step3Title: {
    id: 'Season26ReactivationDialog.step3.title',
    defaultMessage: 'New Lineup Bonuses',
  },
  step3Item1: {
    id: 'Season26ReactivationDialog.step3.list.item1',
    defaultMessage: '4% Score boost for staying under 260 L15 cap',
  },
  step3Item2: {
    id: 'Season26ReactivationDialog.step3.list.item2',
    defaultMessage: '2% Score boost for having ≤2 players from same club',
  },
  step4Title: {
    id: 'Season26ReactivationDialog.step4.title',
    defaultMessage: 'In-Season Competitions',
  },
  step4Item1: {
    id: 'More ways to play Classic',
    defaultMessage:
      '3 dedicated competitions (Champion, All Star, U23), Sealing boosts and Arena with unlimited entries',
  },
  step4Item2: {
    id: 'Season26ReactivationDialog.step4.list.item2',
    defaultMessage:
      '1 Classic slot available inside your In-Season lineups (including for Hot Streaks)',
  },
});

const steps = [
  {
    key: '1',
    title: messages.step1Title,
    item1: messages.step1Item1,
    item2: messages.step1Item2,
    color: 'var(--c-blue-500)',
  },
  {
    key: '2',
    title: messages.step2Title,
    item1: messages.step2Item1,
    item2: messages.step2Item2,
    color: 'var(--c-rewards-orange)',
  },
  {
    key: '3',
    title: messages.step3Title,
    item1: messages.step3Item1,
    item2: messages.step3Item2,
    color: 'var(--c-red-400)',
  },
  {
    key: '4',
    title: messages.step4Title,
    item1: messages.step4Item1,
    item2: messages.step4Item2,
    color: 'var(--c-yellow-300)',
  },
];

export const Season26ReactivationDialog = () => {
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const [open, setOpen] = useState(!!lifecycle?.showS26WelcomeBack);
  const [step, setStep] = useState(0);

  if (!lifecycle?.showS26WelcomeBack) {
    return null;
  }

  const onClose = () => {
    updateLifecycle(LIFECYCLE.showS26WelcomeBack, false);
    setOpen(false);
  };
  const isFirstStep = step === 0;
  const isLastStep = step === 3;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      hideHeader
      style={{ '--background-color': steps[step].color } as CSSProperties}
      footer={
        <FooterButtons>
          <Button
            size="medium"
            disableDebounce
            color="quaternary"
            onClick={() => {
              if (isFirstStep) {
                onClose();
              } else {
                setStep(step - 1);
              }
            }}
          >
            <FormattedMessage
              {...(isFirstStep ? glossary.skip : glossary.back)}
            />
          </Button>
          {isLastStep ? (
            <Button
              size="medium"
              disableDebounce
              color="primary"
              onClick={() => {
                onClose();
              }}
            >
              <FormattedMessage {...glossary.letsGo} />
            </Button>
          ) : (
            <Button
              size="medium"
              disableDebounce
              color="secondary"
              onClick={() => {
                setStep(step + 1);
              }}
            >
              <FormattedMessage {...glossary.next} />
            </Button>
          )}
        </FooterButtons>
      }
    >
      <Root>
        <StepperWrapper>
          <Stepper step={step} length={4} setStep={setStep} />
        </StepperWrapper>
        <Wrapper>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <TitleWrapper>
            <BrandL className="text-center">
              <FormattedMessage
                id="Season26ReactivationDialog.title"
                defaultMessage="Welcome back, Manager – Sorare 26 is here!"
              />
            </BrandL>
          </TitleWrapper>
          <SlidingContentWrapper
            style={{
              transform: `translateX(calc(-100% * ${step})`,
            }}
          >
            {steps.map(({ key, title, item1, item2, color }) => {
              return (
                <Step key={key}>
                  <StepWrapper>
                    <Bezel
                      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, var(--c-black) 0) 0 0/5px 5px"
                      boxShadow={`inset 0px -20px 20px -20px ${color}`}
                    >
                      <StepContent className="text-center">
                        <HeadlineL>
                          <FormattedMessage {...title} />
                        </HeadlineL>
                        <Vertical gap={1} center>
                          {[item1, item2].map((item, index) => {
                            return (
                              // eslint-disable-next-line react/no-array-index-key
                              <BodyM key={index}>
                                <FormattedMessage {...item} />
                              </BodyM>
                            );
                          })}
                        </Vertical>
                      </StepContent>
                    </Bezel>
                  </StepWrapper>
                </Step>
              );
            })}
          </SlidingContentWrapper>
        </Wrapper>
      </Root>
    </StyledDialog>
  );
};
